// AI 教练：场景元信息 + 会话管理接口（设计与计划见 docs/superpowers/specs/2026-09-30-ai-coach-design.md §9）
//
// 全部走项目既有的 axios 封装（httpRequest）：这些接口都是普通 JSON（非流式），
// 流式对话入口在 apis/ai.ts 的 PostAiChat。
// 会话默认私有：所有读写都由服务端带 userId 归属校验，前端不传任何用户身份字段。

import httpRequest from "@/apis/axios-api";
import type { AiAction, AiContextItem } from "@/apis/ai.ts";

/** 一个可用场景的元信息（/ai/scene 的 scenes 元素） */
export interface AiSceneMeta {
  key: string;
  title: string;
  prompt_ver?: string;
  tools?: string[];
  /**
   * 该场景的提示模板（后端场景定义下发，前端渲染成输入框上方的 chips，点击只填充不发送）。
   * 可选字段：后端并行批次尚未下发时前端按空数组处理（渲染为空，不报错）。
   */
  prompts?: string[];
}

/**
 * /ai/scene 的 context 字段：当前对象的上下文预览。
 *
 * 请求里带了 scene + ref 时服务端才会算它（走与对话同一个场景 Prepare，因此与实际注入同源）。
 * available 为 false 时 code 给出原因（项目错误码，查文案表）；items 始终是数组。
 */
export interface AiSceneContextPreview {
  scene: string;
  available: boolean;
  /** available 为 false 时的原因（项目错误码，0 表示无具体错误码） */
  code?: number;
  items: AiContextItem[];
}

/** /ai/scene 返回：可用场景 + BYOK 是否就绪（未就绪时前端引导去设置页） */
export interface AiSceneMetaResult {
  scenes: AiSceneMeta[];
  byok_ready: boolean;
  provider?: string;
  model?: string;
  /** 当前对象的上下文预览：请求带了 scene + ref 时才有值（旧后端不返回） */
  context?: AiSceneContextPreview;
}

/** 会话列表/详情里的会话元信息 */
export interface AiConversationItem {
  id: number;
  scene: string;
  /** judge / problem / contest / ''（空表示不关联具体对象） */
  ref_type: string;
  ref_id: number;
  /**
   * 打开教练时所在页面（路由 name，如 problem-detail）；旧数据为空串。
   * 统计口径（「用户从哪里问起」）与会话详情展示共用。
   */
  entry_page?: string;
  /**
   * 打开教练时所在页面的真实访问路径（route.path + 查询参数名，如 /problem/P1001、
   * /judge/77?status&username；查询参数的取值不入库）；旧数据为空串。
   * 与 entry_page 一起落库，统计里用来看「用户具体在哪道题 / 哪次提交上问起」。
   */
  entry_path?: string;
  /**
   * 题目引用的题目编号（key）。题目详情页按 key 查询，而 ref_id 是内部自增 id，
   * 因此只有拿到 key 才能生成可用链接；后端未补该字段时为空（前端按纯文本展示）。
   */
  problem_key?: string;
  title: string;
  message_count: number;
  total_tokens: number;
  insert_time: string;
  modify_time: string;
}

/** assistant 消息携带的一次工具调用请求 */
export interface AiToolCallItem {
  call_id: string;
  name: string;
  /** 模型给出的原始 JSON 文本：不保证合法，仅用于展示 */
  arguments: string;
}

/** role=tool 消息对应的工具结果 */
export interface AiToolResultItem {
  call_id: string;
  name: string;
  content: string;
}

/** 会话内一条消息（user / assistant / tool 共用结构） */
export interface AiMessageView {
  id: number;
  role: string;
  content: string;
  thinking: string;
  tool_calls: AiToolCallItem[];
  tool_results: AiToolResultItem[];
  actions: AiAction[];
  provider: string;
  model: string;
  total_tokens: number;
  /** 该轮输入中命中厂商上下文缓存的部分（计费折扣）；厂商不返回时为 0，历史消息未采集时也为 0 */
  cached_tokens: number;
  insert_time: string;
}

/** GET /ai/conversation?id= 的返回 */
export interface AiConversationDetail {
  conversation: AiConversationItem;
  messages: AiMessageView[];
}

/** 会话列表的游标分页参数（cursor_time + cursor_id 取上一页最后一行的值） */
export interface AiConversationCursor {
  cursor_time?: string;
  cursor_id?: number;
  limit?: number;
}

/** GET /ai/conversation/list 的返回 */
export interface AiConversationPage {
  list: AiConversationItem[];
  has_more: boolean;
  next_cursor_time: string;
  next_cursor_id: number;
}

/**
 * 场景元信息：可用场景列表与 BYOK 是否就绪。
 *
 * 带上 scene + ref 时，服务端会顺带返回该对象的上下文预览（context）——
 * 用户「它到底看得到什么」的疑问出现在**发问之前**，所以要在这里就能拿到。
 * 不带参数时行为与旧版本一致（只返回场景列表）。
 */
export function GetAiScene(params?: { scene?: string; ref?: { type: string; id: number } }) {
  const data: Record<string, unknown> = {};
  if (params?.scene) {
    data.scene = params.scene;
  }
  if (params?.ref && (params.ref.type || params.ref.id > 0)) {
    data.ref = params.ref;
  }
  return httpRequest({
    url: "/ai/scene",
    method: "post",
    data,
  });
}

/** 我的会话列表（按 modify_time 倒序游标分页） */
export function GetAiConversationList(cursor?: AiConversationCursor) {
  const params: Record<string, string> = {};
  if (cursor?.cursor_time) {
    params.cursor_time = cursor.cursor_time;
  }
  if (cursor?.cursor_id) {
    params.cursor_id = String(cursor.cursor_id);
  }
  if (cursor?.limit) {
    params.limit = String(cursor.limit);
  }
  return httpRequest({
    url: "/ai/conversation/list",
    method: "get",
    params,
  });
}

/** 会话详情（仅本人；不存在或非本人返回 100096） */
export function GetAiConversation(id: number) {
  return httpRequest({
    url: "/ai/conversation",
    method: "get",
    params: { id: String(id) },
  });
}

/** 重命名会话（仅本人；不存在或非本人返回 100096） */
export function PostAiConversationRename(id: number, title: string) {
  return httpRequest({
    url: "/ai/conversation/rename",
    method: "post",
    data: { id, title },
  });
}

/** 删除会话及其全部消息（仅本人；不存在或非本人返回 100096） */
export function PostAiConversationDelete(id: number) {
  return httpRequest({
    url: "/ai/conversation/delete",
    method: "post",
    data: { id },
  });
}
